/**
 * The sends this browser is following (MA-484), as rows with a live countdown.
 *
 * TrackedSendList renders the store for one origin; MailSendTray is the
 * floating copy mounted once in the app shell for surfaces that close as soon
 * as they send (the inbox reply), so the Undo stays reachable after the reply
 * box has gone.
 */
import { MailSendRow } from './MailSendRow';
import {
  describeSend,
  dismissTrackedSend,
  resolveTrackedSend,
  undoTrackedSend,
  useNow,
  useTrackedSends,
  type SendOrigin,
} from './mailSend';

export function TrackedSendList({ origin, className }: { origin: SendOrigin; className?: string }) {
  const sends = useTrackedSends(origin);
  const now = useNow(1_000, sends.length > 0);
  if (sends.length === 0) return null;
  return (
    <div className={className ?? 'space-y-2'} data-testid={`tracked-sends-${origin}`}>
      {sends.map(s => (
        <MailSendRow
          key={s.id}
          draft={s.draft}
          view={describeSend(s.draft, now, { undoUntilMs: s.undoUntilMs })}
          notice={s.notice}
          busy={s.busy}
          onUndo={() => void undoTrackedSend(s.id)}
          onResolve={action => void resolveTrackedSend(s.id, action)}
          onDismiss={() => dismissTrackedSend(s.id)}
        />
      ))}
    </div>
  );
}

export function MailSendTray() {
  const sends = useTrackedSends('tray');
  if (sends.length === 0) return null;
  return (
    <div
      className="fixed bottom-4 left-4 z-50 w-[26rem] max-w-[calc(100vw-2rem)] bg-white shadow-lg rounded-xl p-2"
      role="region"
      aria-label="Sending"
    >
      <TrackedSendList origin="tray" />
    </div>
  );
}
